import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, MessageSquare, ChevronDown } from "lucide-react";
import { useCmsContent, renderMarkers } from "@/lib/cms";
import SeoCopy from "@/components/SeoCopy";

export default function ContactPage() {
  const { c } = useCmsContent("contact");
  const faq = c["contact.faq"] || [];
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(null);

  const send = async (e) => {
    e.preventDefault();
    if (!form.email || !form.message) return;
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      toast.success("Got it — we'll get back to you soon!");
      setForm({ name: "", email: "", message: "" });
    } catch { toast.error("Could not send"); }
    setSubmitting(false);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 flex items-center gap-2" data-testid="contact-title">
        <MessageSquare strokeWidth={2.5}/> {c["contact.title"]}
      </h1>

      <form onSubmit={send} className="card-clay p-6 mt-4 space-y-3" data-testid="contact-form">
        <h2 className="font-fredoka font-bold text-lg">{c["contact.form_title"]}</h2>
        <input className="input-clay h-11 px-3 w-full" placeholder="Your name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="contact-name"/>
        <input type="email" required className="input-clay h-11 px-3 w-full" placeholder="Email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="contact-email"/>
        <textarea required rows={5} className="input-clay px-3 py-2 w-full" placeholder={c["contact.form_placeholder"] || "How can we help?"} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} data-testid="contact-message"/>
        <button disabled={submitting} className="btn-push-green px-5 py-2.5 inline-flex items-center gap-2" data-testid="contact-submit"><Mail size={16}/> {submitting ? "Sending…" : "Send"}</button>
      </form>

      <div className="mt-8">
        <h2 className="font-fredoka font-bold text-xl mb-3">{c["contact.faq_title"]}</h2>
        <div className="space-y-2" data-testid="faq-list">
          {faq.map((f, i) => {
            const q = f.title;
            const a = (f.bullets || []).join(" ");
            return (
              <div key={i} className="card-clay overflow-hidden">
                <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left" data-testid={`faq-toggle-${i}`}>
                  <span className="font-fredoka font-bold text-slate-900">{q}</span>
                  <ChevronDown className={`transition-transform ${open === i ? "rotate-180" : ""}`}/>
                </button>
                {open === i && <div className="px-5 pb-4 text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: renderMarkers(a) }}/>}
              </div>
            );
          })}
        </div>
        <SeoCopy
          title="Contact Bag Dash Kids"
          links={[
            { to: "/about", label: "Meet the Kids" },
            { to: "/map", label: "Service area" },
            { to: "/policies", label: "Policies & FAQ" },
            { to: "/rules", label: "Service rules" },
          ]}
        >
          <p>Bag Dash Kids is parent-managed and operated by Big Sib Josiah and Lil Sib Jayla in Charlotte, NC. For pickup questions, scheduling conflicts, billing or refunds, weekly subscription changes, missed pickups, or to request service in a new community, send a message here and Mom Manager Donja or the team will respond within one business day.</p>
          <p>Communities not yet on our route? Use the <strong>Service Area Map</strong> page to join the waitlist — we expand based on demand.</p>
        </SeoCopy>
      </div>
    </div>
  );
}
